import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './MannequinAdjustSlide.css';
import FitMannequin from './FitMannequin';
import { computeProfile } from '../services/sizeRecommendationService';
import {
  computeBandConfig,
  getMorphologyCategory,
  getMannequinSrc,
} from '../utils/measurementUtils';
import logger from '../services/logger';

const SLIDER_COLORS = {
  chest: '#3a3a3a',
  waist: '#525252',
  hips:  '#6b6b6b',
};

const TICK_VALUES = [-2, -1, 0, 1, 2];

function MannequinAdjustSlide({ onNext, onBack, initialData, gender, height, weight, age, existingMeasurements }) {
  const { t: tc } = useTranslation('common');

  const [deltaChest, setDeltaChest] = useState(initialData?.deltaChest ?? 0);
  const [deltaWaist, setDeltaWaist] = useState(initialData?.deltaWaist ?? 0);
  const [deltaHips,  setDeltaHips]  = useState(initialData?.deltaHips  ?? 0);

  const [morphologyMeasurements, setMorphologyMeasurements] = useState(existingMeasurements || null);

  useEffect(() => {
    if (existingMeasurements || !gender || !height || !weight) return;
    let cancelled = false;
    const loadMorphology = async () => {
      try {
        const m = await computeProfile({ gender, height, weight, age,
          deltaChest: 0, deltaWaist: 0, deltaHips: 0 });
        if (!cancelled) setMorphologyMeasurements(m);
      } catch (err) {
        logger.warn('Morphologie non disponible pour la sélection du mannequin:', err);
      }
    };
    loadMorphology();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const category     = getMorphologyCategory(morphologyMeasurements, height, weight);
  const mannequinSrc = getMannequinSrc(gender, category);
  const genderCode   = gender === 'homme' ? 'H' : 'F';

  const bandConfig = useMemo(
    () => computeBandConfig(deltaChest, deltaWaist, deltaHips, gender, category),
    [deltaChest, deltaWaist, deltaHips, gender, category]
  );

  const adjustFitZones = [
    { zone: 'chest', color: SLIDER_COLORS.chest, labelKey: null },
    { zone: 'waist', color: SLIDER_COLORS.waist, labelKey: null },
    { zone: 'hips',  color: SLIDER_COLORS.hips,  labelKey: null },
  ];

  const handleConfirm = () => {
    onNext({ deltaChest, deltaWaist, deltaHips });
  };

  const sliders = [
    { key: 'chest', label: tc('mannequin.chest'), value: deltaChest, set: setDeltaChest, color: SLIDER_COLORS.chest },
    { key: 'waist', label: tc('mannequin.waist'), value: deltaWaist, set: setDeltaWaist, color: SLIDER_COLORS.waist },
    { key: 'hips',  label: tc('mannequin.hips'),  value: deltaHips,  set: setDeltaHips,  color: SLIDER_COLORS.hips  },
  ];

  return (
    <div className="slide mannequin-slide">
      <div className="slide-content">
        <h2>{tc('mannequin.title')}</h2>
        <p className="slide-description">
          {tc('mannequin.description')}
        </p>

        <div className="mannequin-container">
          <FitMannequin
            fitType="ideal"
            gender={gender}
            garmentType="full"
            fitZones={adjustFitZones}
            mannequinSrc={mannequinSrc}
            customBandConfig={bandConfig}
          />

          <div className="mannequin-sliders">
            {sliders.map(({ key, label, value, set, color }) => {
              return (
                <div key={key} className="slider-group">
                  <div className="slider-header">
                    <span className="slider-label">{label}</span>
                  </div>

                  <div className="slider-dots-row">
                    <span className="slider-hint-label">{tc('mannequin.slimmer')}</span>
                    <img
                      src={`/body_part/${key}_${genderCode}_slim.jpg`}
                      alt=""
                      className="body-part-img"
                    />
                    <div className="slider-dots">
                      {TICK_VALUES.map(tick => (
                        <button
                          key={tick}
                          className={`slider-dot${value === tick ? ' slider-dot--active' : ''}`}
                          style={value === tick
                            ? { backgroundColor: color, borderColor: color }
                            : { borderColor: color }
                          }
                          onClick={() => set(tick)}
                        />
                      ))}
                    </div>
                    <img
                      src={`/body_part/${key}_${genderCode}_large.jpg`}
                      alt=""
                      className="body-part-img"
                    />
                    <span className="slider-hint-label slider-hint-label--right">{tc('mannequin.larger')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="navigation-buttons">
          {onBack && (
            <button className="btn btn-secondary" onClick={onBack}>
              {tc('buttons.back')}
            </button>
          )}
          <button className="btn btn-primary" onClick={handleConfirm}>
            {tc('buttons.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MannequinAdjustSlide;
